import type { Types as AmplitudeTypes } from "@amplitude/analytics-browser";
import { debounce } from "@solid-primitives/scheduled";
import type { Navigator } from "@solidjs/router";
import { useQuery } from "@tanstack/solid-query";
import { isAxiosError } from "axios";
import { createEffect, createMemo, on, untrack } from "solid-js";
import { signOut } from "supertokens-web-js/lib/build/recipe/thirdpartyemailpassword";
import type { Primitive } from "zod";
import { config } from "~/js/network/network-handler";
import { addPrefixToKeys } from "~/js/utilities/addPrefixToKeys";
import EventAnalytics, { GrayLog } from "~/js/utilities/EventAnalytics";
import { getUserStore, setUserStore } from "~/solidJs/entities/HeaderMain/models";
import { TeamMethods } from "~/solidJs/entities/Teams";
import { CreditsApi } from "../api/CreditsApi";
import { PaymentApi } from "../api/PaymentApi";
import { queryClient } from "../api/queryClient";
import { TeamApi, type TeamInfoResponse, type TeamMember } from "../api/TeamApi";
import { UserApi } from "../api/user";
import { CustomRuntimeError } from "../models/errors/runtimeErrors";
import { newIdenifyEvent, sendIdentifyEvent } from "./amplitudeHelpers";
import { getCookie } from "./cookiesHelpers";
import { formatCurrency } from "./currencyHelpers";
import { formatDateMonthDayAtTime, parseDateToReadableString } from "./dateHelpers";
import { getPrivateMessageFromError } from "./errorHelpers";
import { foundDiffBetweenTwoPrimitiveObjects, type primitives } from "./objectHelpers";
import { capitalizeFirstLetter } from "./stringHelpers";
import { DebugFlags, isDebugFlag } from "./testHelpers";
import {
	type IUser,
	SubscriptionPlanStatusEnum,
	type UserOnlineInformation,
	UserSubscriptionPlanEnum
} from "./types";
import type { FilterObjectByValue } from "./types/ObjectHelpersTypes";

export const DEFAULT_DISABLED_GROUPS = {
	free: [
		UserSubscriptionPlanEnum.Free,
		UserSubscriptionPlanEnum.Legacy_Freemium,
		UserSubscriptionPlanEnum.Legacy_Trialing
	]
} as const satisfies Record<string, UserSubscriptionPlanEnum[]>;

export const getIsAccesDisabled = (...disabledGroups: UserSubscriptionPlanEnum[]) => {
	const group = getUserStore()?.group;

	if (!group) return true;
	for (const disabledGroup of disabledGroups) {
		if (group === disabledGroup) return true;
	}
	return false;
};
export function isEmailVerified(tokenPayload?: Record<string, any> | null) {
	return tokenPayload?.["st-ev"]?.v;
}

export async function getUserInfo(): Promise<IUser | null> {
	const refreshToken = getCookie("st-refresh-token");
	if (refreshToken === null) {
		return null;
	}
	const session = await UserApi.getUserInitInfo();
	if (!session) return null;

	return session;
}

export async function getUserOnlineInfo(props?: {
	retryDelay?: number;
}): Promise<UserOnlineInformation | null> {
	const refreshToken = getCookie("st-refresh-token");
	if (refreshToken === null) {
		return null;
	}
	const session = await UserApi.getUserOnlineInfo(props);
	if (!session) return null;

	return session;
}

export async function getUserAndSetStore() {
	const user = await getUserInfo();
	setUserStore(user);
	return user;
}

export async function revalidateUserOnlineInfo(props?: { retryDelay?: number }) {
	const userOnline = await getUserOnlineInfo(props);
	if (!userOnline) return;
	setUserStore((prev) => {
		if (!prev) return userOnline as IUser;
		return { ...prev, ...userOnline } as IUser;
	});
	return userOnline;
}

export const debounceRevalidateUserOnlineInfo = debounce(
	() => revalidateUserOnlineInfo().catch(() => null),
	1000
);

export function isUserInited(user: IUser | null) {
	return user && !!user.id;
}

export function createRevalidateUserOnlineInfoInterval(modalMethods?: {
	show: () => void;
	close: () => void;
}) {
	let interval: NodeJS.Timeout;
	let lastRevalidation = 0;
	let intervalTimeout = 10000;
	let isInErrorState = false;
	const onRevalidateOnlineInfo = async () => {
		const startRevalidating = Date.now();
		if (startRevalidating - lastRevalidation < intervalTimeout) {
			return;
		}

		lastRevalidation = Date.now();
		try {
			await revalidateUserOnlineInfo({ retryDelay: isInErrorState ? 5000 : 1000 });
			intervalTimeout = 10000;
			if (isInErrorState) {
				modalMethods?.close();
			}
			isInErrorState = false;
		} catch (e) {
			let error = e;
			if (e instanceof CustomRuntimeError) {
				error = e.cause;
			}
			if (isAxiosError(error) && (error.status === 500 || error.code === "ERR_NETWORK")) {
				intervalTimeout = 20000;
				if (!isInErrorState) {
					modalMethods?.show();
				}
				isInErrorState = true;
			}
		}
	};

	const start = () => {
		interval = setInterval(onRevalidateOnlineInfo, 10000);
		window.addEventListener("focus", onRevalidateOnlineInfo);
	};

	const stop = () => {
		clearInterval(interval);
		window.removeEventListener("focus", onRevalidateOnlineInfo);
	};

	return { start, stop };
}

export function isAuthenticated(user?: IUser | null): boolean {
	return !!user;
}

export function verifyUser() {
	setUserStore((prev) => {
		if (!prev) return prev;
		prev["st-ev"] = {
			v: true,
			t: Date.now()
		};
		return { ...prev };
	});
}

export function isUserNeedToSetupAccount(user: IUser | null): boolean {
	return (
		isDebugFlag(DebugFlags.SetupAccontDebug) ||
		(!!user && (user.questionnaire === null || user.questionnaire === false))
	);
}

export function isUserHasAccess(user: IUser | null, accessMap: Record<string, boolean>): boolean {
	if (!user) {
		return false;
	}
	return user.group in accessMap;
}
/**
 * @description gets payments from the server and send info to analitycs system
 */
export async function registratePaymentsInAnalitycs() {
	if (config?.IS_PRODUCTION !== "true") {
		return;
	}
	let payments: Awaited<ReturnType<typeof PaymentApi.getUserPayments>> | undefined;
	try {
		payments = await PaymentApi.getUserPayments();
	} catch (e) {
		console.error(e);
		EventAnalytics.sendErrorInfo("get-payments-error", {
			message: getPrivateMessageFromError(e)
		});
		return;
	}
	if (!payments?.length) {
		return;
	}
	try {
		for (const payment of payments) {
			try {
				GrayLog.debug({
					log_name: "purchase",
					message: "send purchase event",
					isNeedToSendToAnalitics: false,
					price: payment.price,
					transaction_id: payment.transaction_id,
					code: payment.discount_code
				});
			} catch (_e) {}
			EventAnalytics.purchace(payment.price, payment.transaction_id, payment.discount_code);
			EventAnalytics.checkoutCompleted(payment.subscription_name);
		}
	} catch (e) {
		console.error(e);
		EventAnalytics.sendErrorInfo("send-payments-error", {
			message: getPrivateMessageFromError(e)
		});
	}
}

export const isUserFree = (user?: IUser | null) => {
	return (
		user?.group === UserSubscriptionPlanEnum.Legacy_Freemium ||
		user?.group === UserSubscriptionPlanEnum.Legacy_Trialing ||
		user?.group === UserSubscriptionPlanEnum.Free
	);
};

export const getUserSubscriptionStatus = (user?: IUser | null) => {
	return user?.subscription_status;
};

export const isUserFrozen = (user?: IUser | null) => {
	return getUserSubscriptionStatus(user) === "frozen";
};

export const isUserCanUseCredits = (user?: IUser | null) => {
	if (!user) return false;
	if (isUserFree(user)) return false;
	if (getIsUserOnLegacyPlan(user)) return false;
	return true;
};

export const getUserGenerationsAmount = (user: Partial<IUser> | null) => {
	if (!user || !user.available_runs || !user.total_runs) return 0;
	return user.available_runs;
};

export const getIsUserHasGenerations = async () => {
	const onlineInfo = await revalidateUserOnlineInfo().catch(() => null);
	if (!onlineInfo) return false;
	return getUserGenerationsAmount(onlineInfo) > 0;
};

const OAuthRestoreLocationKey = "OAuthRestoreLocationKey";

export const captureRedirectAuthLocation = () => {
	const url = new URL(location.href);
	const redirectTo = url.searchParams.get("from");
	if (!redirectTo) {
		localStorage.removeItem(OAuthRestoreLocationKey);
		return;
	}
	localStorage.setItem(OAuthRestoreLocationKey, redirectTo);
};

export const resotreRedirectAuthLocation = (navigate: Navigator) => {
	let out = false;
	const redirectTo = localStorage.getItem(OAuthRestoreLocationKey);
	if (redirectTo) {
		navigate(redirectTo, { replace: true });
		out = true;
	}

	localStorage.removeItem(OAuthRestoreLocationKey);
	return out;
};

const disabledPropertiesLookUp = {
	status: true,
	start_time: true,
	end_time: true,
	"st-ev": true,
	subscription: true,
	group: true,
	next_payment: true,
	credits: true
} as const satisfies Partial<Record<keyof IUser, boolean>>;

export const getNonDisabledUserFields = (user: IUser) => {
	const record = {} as FilterObjectByValue<IUser, primitives>;
	for (const key in user) {
		if (
			key in disabledPropertiesLookUp ||
			typeof (user as any)[key] === "object" ||
			Array.isArray((user as any)[key])
		) {
			continue;
		}
		// @ts-expect-error
		record[key] = (user as any)[key];
	}
	return record;
};

/**
 * Remember last user info in local storage
 * if there is no user, return last remembered user
 */
export const rememberDangerousUser = (user?: IUser): IUser | null => {
	if (!user) {
		const userFromLocalStorage = localStorage.getItem("user");
		if (userFromLocalStorage) {
			try {
				const parsedUser = JSON.parse(userFromLocalStorage);
				if (parsedUser && typeof parsedUser === "object") {
					return parsedUser as IUser;
				}
			} catch (_error) {}
		}
		return null;
	}
	localStorage.setItem("user", JSON.stringify(user));

	return user;
};

export const getAmplitudeReadyObject = <ObjT extends Record<string, Primitive>>(
	obj: ObjT
): FilterObjectByValue<ObjT, AmplitudeTypes.ValidPropertyType> => {
	const out = {} as FilterObjectByValue<ObjT, AmplitudeTypes.ValidPropertyType>;
	for (const key in obj) {
		const value = obj[key];
		if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
			// @ts-expect-error
			out[key] = value;
		}
	}
	return out;
};

export const createUserGroupChangeEffect = (props: {
	onGroupChange: (user: IUser | null) => void;
}) => {
	createEffect(
		on(
			() => getUserStore()?.group,
			(group, _, prev) => {
				if (!prev || !group || group === prev) return group;
				props.onGroupChange(getUserStore());
				return group;
			},
			{ defer: true }
		),
		untrack(() => getUserStore()?.group)
	);
};

export const createUserIdenifyEffect = () => {
	createEffect((prev: FilterObjectByValue<IUser, primitives> | undefined | null) => {
		const user = getUserStore();
		if (!user) return user;
		const primitveUser = getNonDisabledUserFields(user);
		if (primitveUser.banned_at) {
			signOut();
			document.querySelector("#banned-banner")?.classList?.remove("hidden");
			document.querySelector("#banned-banner")?.classList?.add("flex");
			return;
		}
		const prefixedUser = addPrefixToKeys(primitveUser, "user_");
		let identifyEvent: AmplitudeTypes.Identify;
		if (prev) {
			const diff = foundDiffBetweenTwoPrimitiveObjects(
				addPrefixToKeys(prev, "user_"),
				prefixedUser
			);
			if (diff.length < 1) return primitveUser;
			identifyEvent = newIdenifyEvent(getAmplitudeReadyObject(Object.fromEntries(diff)));
			if (config.FRONT_CANARY) {
				identifyEvent.set("isCanary", config.FRONT_CANARY);
			}
		} else {
			identifyEvent = newIdenifyEvent(getAmplitudeReadyObject(prefixedUser));
			if (config.FRONT_CANARY) {
				identifyEvent.set("isCanary", config.FRONT_CANARY);
			}
		}
		sendIdentifyEvent(identifyEvent, user);
		if (config.IS_PRODUCTION !== "true") {
			console.log("send-idenify", identifyEvent);
		}

		return primitveUser;
	});
};

export const isUserFromRussia = (user: IUser | null) => {
	return isGeoRussia(user?.geo);
};

export const isGeoRussia = (geo?: string | null) => {
	return geo === "Russia";
};

export const isNeedToShowGenerationLimitsCounter = () => {
	const user = getUserStore();
	if (isUserFrozen(user)) return false;
	if (isUserFree(user)) {
		return true;
	}
	return !!user?.become_available_at;
};

/**
 * Hook to check if upgrade button should be shown
 * Hides upgrade button for all team members (owner and teammates)
 * Must be used inside a component
 */
export const useIsNeedToShowUpgrade = () => {
	const teamInfo = useQuery(() => TeamApi.getTeamInfoOptions());

	return createMemo(() => {
		// If query is still loading or errored, show button by default (safe fallback)
		if (teamInfo.isLoading) return isNeedToShowGenerationLimitsCounter();
		if (teamInfo.isError) return isNeedToShowGenerationLimitsCounter();

		// Hide for all team members (owner and teammates)
		if (teamInfo.data) return false;

		// Otherwise use the standard logic
		return isNeedToShowGenerationLimitsCounter();
	});
};

export const isUserFromAppSumo = (user: IUser | null) => {
	return user?.group.startsWith("app_sumo");
};

export const getAvatarLetter = (user: IUser | null) => {
	if (!user) return "";
	return user?.email?.[0].toUpperCase() || "";
};

export const getUserPlanName = (user: IUser | null) => {
	if (!user) return "";
	return user?.subscription_name || capitalizeFirstLetter(user?.group || "");
};

// Returns plan name without "Frozen" suffix and snowflake emoji
export const getUserPlanNameClean = (user: IUser | null) => {
	const planName = getUserPlanName(user);

	// For old tariff plans (plan_status === "old"), show "Legacy Plan" instead of technical names
	if (user?.plan_status === SubscriptionPlanStatusEnum.Old) {
		return "Legacy Plan";
	}

	// Remove snowflake emoji and "Frozen" text
	return planName.replace(/\s*❄\s*Frozen\s*/gi, "").trim();
};

export const getUserName = (user: IUser | null) => {
	if (!user) return "";
	return user?.email.slice(0, 5);
};

export const getUserCreditsBalance = (user: IUser | null) => {
	if (!user) return 0;
	if (getIsUserInfinity(user) || getIsTeamMemberInfinity(user)) {
		return getUserCreditsTotal(user);
	}
	return user?.credits?.credits_balance || 0;
};

export const getUserCreditsTotal = (user: IUser | null) => {
	if (!user) return 0;
	return (
		user?.credits?.credits_balance_breakdowns.reduce(
			(acc, item) => acc + item.credits_received,
			0
		) || 0
	);
};

export const getUserCreditsRefreshText = (user: IUser | null) => {
	if (!user) return "";
	return `Your ${user.credits.count_credits_by_sub} credits will refresh on ${getCreditsRefreshReadebleString(user)}`;
};

export const getNextBillingText = (user: IUser | null) => {
	if (!user) return "";
	if (isUserFromAppSumo(user) && getIsUserOnLegacyPlan(user)) {
		return "Your subscription is Active";
	}
	const isFree = isUserFree(user);
	const textParts: string[] = [];
	const expirationDate = user.credits.expiration_date;

	if (expirationDate) {
		const formattedDate = formatDateMonthDayAtTime(new Date(expirationDate));

		if (isFree) {
			textParts.push(`Your credits refresh weekly on ${formattedDate}.`);
		} else if (user.group === UserSubscriptionPlanEnum.AppSumoTier4) {
			textParts.push(`Your credits refresh on ${formattedDate}.`);
		} else {
			textParts.push(`Your credits refresh monthly on ${formattedDate}.`);
		}
	}

	if (user.next_payment) {
		const [dateStr, amount] = Object.entries(user.next_payment)[0] || [];

		if (dateStr && amount !== undefined) {
			const formattedDate = new Date(dateStr).toLocaleDateString("en-US", {
				month: "long",
				day: "numeric",
				year: "numeric"
			});
			if (user.subscription === "yookassa") {
				textParts.push(
					`Next billing date is ${formattedDate} for ${formatCurrency(amount.toString(), "RUB")}`
				);
			} else {
				textParts.push(
					`Next billing date is ${formattedDate} for ${formatCurrency(amount.toString(), "USD")}`
				);
			}
		}
	}

	return textParts.join(" ");
};
export const getNextBillingLegacyText = (user: IUser | null) => {
	if (!user || !getIsUserOnLegacyPlan(user)) return "";
	if (isUserFromAppSumo(user)) {
		return "Your subscription is Active";
	}
	const textParts: string[] = [];
	const nextInfo = user.upgrade_info;
	const upgradeText = nextInfo?.new_plan ? ` for ${nextInfo.new_plan} plan` : "";
	if (user.next_payment) {
		const [dateStr, amount] = Object.entries(user.next_payment)[0] || [];

		if (dateStr && amount !== undefined) {
			const formattedDate = new Date(dateStr).toLocaleDateString("en-US", {
				month: "long",
				day: "numeric"
			});
			if (user.subscription === "yookassa") {
				textParts.push(
					`Your subscription will get renewed on ${formattedDate} for ${formatCurrency(amount.toString(), "RUB")}${upgradeText}`
				);
			} else {
				textParts.push(
					`Your subscription will get renewed on ${formattedDate} for ${formatCurrency(amount.toString(), "USD")}${upgradeText}`
				);
			}
		}
	}

	return textParts.join(" ");
};

export const getCreditsRefreshReadebleString = (user: IUser | null) => {
	if (!user) return "";

	if (user.credits.expiration_date) {
		const formattedDate = new Date(user.credits.expiration_date).toLocaleDateString("en-US", {
			month: "long",
			day: "numeric"
		});
		return formattedDate;
	}

	return "";
};

export const getUserEndDate = (user: IUser | null): string => {
	if (!user?.end_time) return "";
	return parseDateToReadableString(new Date(user.end_time));
};

export const getIsUserInfinity = (user: IUser | null) => {
	if (!user) return false;
	return user.credits.is_infinity;
};

export const getIsTeamMemberInfinity = (user: IUser | null) => {
	if (!user) return false;
	// Only check for team members (frozen users)
	if (!isUserFrozen(user)) return false;

	// Get cached team info
	const teamInfo = queryClient.getQueryData<TeamInfoResponse | null>(["teamInfo", user.id]);
	if (!teamInfo?.members) return false;

	// Find current member
	const currentMember = teamInfo.members.find((m: TeamMember) => m.user_id === user.id);
	return currentMember?.is_infinity ?? false;
};

export const getIsUserOnLegacyPlan = (user?: IUser | null) => {
	if (!user) return false;
	return user.plan_status === SubscriptionPlanStatusEnum.Old;
};

export const mutateUserCredits = (amount: number) => {
	setUserStore((prev) => {
		if (!prev) return prev;
		return {
			...prev,
			credits: {
				...(prev.credits || {}),
				credits_balance: prev.credits.credits_balance + amount
			}
		};
	});
};

export const getAvailableCreditsBalance = (user: IUser | null): number => {
	if (!user) return 0;

	// If user is NOT frozen, always use personal credits
	if (user.subscription_status !== "frozen") {
		// Check if personal account has is_infinity
		if (user.credits?.is_infinity) {
			return Number.MAX_SAFE_INTEGER;
		}
		return getUserCreditsBalance(user) ?? 0;
	}

	// If user IS frozen, try to use team credits (if team is active)
	const teamInfo = queryClient.getQueryData<TeamInfoResponse | null>(["teamInfo", user.id]);

	const isTeamSubscriptionActive =
		teamInfo?.team_subscription_status === "active" ||
		teamInfo?.team_subscription_status === "cancelled" ||
		teamInfo?.team_subscription_status === "due";

	if (teamInfo?.company_status === "active" && isTeamSubscriptionActive) {
		const currentMember = teamInfo.members?.find((m) => m.user_id === user.id);
		if (currentMember) {
			// If team member has is_infinity, return a large number to indicate unlimited credits
			if (currentMember.is_infinity) {
				return Number.MAX_SAFE_INTEGER;
			}
			return currentMember.credits_balance;
		}
	}

	// Fallback to personal credits if no team credits available
	return getUserCreditsBalance(user) ?? 0;
};

/**
 * Check if user should see premium lock on features
 * Returns true if user is free AND doesn't have team credits
 */
export const shouldShowPremiumLock = (user: IUser | null): boolean => {
	if (!user) return true;

	// If user is not free, they have access
	if (!isUserFree(user)) return false;

	// If user is free, check if they have team credits
	const availableCredits = getAvailableCreditsBalance(user);
	return availableCredits <= 0;
};

export const isUsercanRunPricedTask = (price: number, user: IUser | null) => {
	if (!user) return false;
	// Check personal infinity credits
	if (getIsUserInfinity(user)) return true;
	// Check team member infinity credits
	if (getIsTeamMemberInfinity(user)) return true;
	// For legacy plans (old tariff), credits are not used - they use task limits instead
	if (getIsUserOnLegacyPlan(user)) return true;
	// Check if user has enough credits
	return getAvailableCreditsBalance(user) >= price;
};

export const getTariffTransitionText = (user: IUser | null) => {
	if (!user || !user.upgrade_info?.upgrade_ts || !user.upgrade_info?.new_plan) return null;
	return `${user.upgrade_info.new_plan} from ${parseDateToReadableString(new Date(user.upgrade_info.upgrade_ts))}`;
};

export const getTariffTransitionDeltailedText = (user: IUser | null) => {
	if (!user || !user.upgrade_info?.upgrade_ts || !user.upgrade_info.new_plan) return null;
	return `Your subscription is active until ${parseDateToReadableString(new Date(user.upgrade_info.upgrade_ts))} and then will switch to ${user.upgrade_info.new_plan} plan.`;
};

export const getUserSubscriptionLength = (user: IUser | null) => {
	if (!user) return null;
	return user.sub_length;
};

/**
 * Get team company status from query cache
 * Returns cached team info status without making a new request
 */
export const getTeamCompanyStatusFromCache = (): "active" | "inactive" | null => {
	const userId = getUserStore()?.id;
	if (!userId) return null;

	// Get cached team info using the same query key pattern as TeamApi.getTeamInfoOptions
	const teamInfo = queryClient.getQueryData<TeamInfoResponse | null>(["teamInfo", userId]);
	return teamInfo?.company_status ?? null;
};

export const getIsSaveFunctionalityAvailable = (user: IUser | null) => {
	if (!user) return false;

	// Team membership takes priority - even frozen personal accounts can save if in active team
	const teamStatus = getTeamCompanyStatusFromCache();
	if (teamStatus === "active") return true;

	// User can save if they have active personal subscription (not free and not frozen)
	if (!isUserFree(user) && !isUserFrozen(user)) return true;

	return false;
};

/**
 * Hook to track user credits balance and total credits in tariff
 * Important to wrap component that will use this hook into suspense
 * Handles frozen user subscription by checking team credits
 * Uses lightweight CreditsApi endpoints instead of full team info
 * @returns Object with remainCredits (current balance) and totalCredits (total in tariff)
 */
export const useUserCredits = () => {
	const user = createMemo(() => getUserStore());

	// Use lightweight credits endpoints instead of full team info
	const personalCredits = useQuery(() => ({
		...CreditsApi.getOnlineCreditsOptions(),
		enabled: !!user() && user()?.subscription_status === "active",
		suspense: false
	}));

	const teamCredits = useQuery(() => ({
		...CreditsApi.getTeamCreditsInfoOptions(),
		enabled: !!user() && user()?.subscription_status === "frozen",
		suspense: false
	}));

	// Memoize current user's team member entry to avoid duplicate lookups
	const currentUserMember = createMemo(() => {
		const currentUser = user();
		if (!currentUser || !teamCredits.data) return null;
		// user_id in TeamMember is string | null, currentUser.id is string
		return teamCredits.data.members?.find((m) => m.user_id === currentUser.id) || null;
	});

	// Get team member credits (only for frozen users - team members, not owner)
	const teamMemberCreditsBalance = createMemo(() => {
		const currentUser = user();
		// Only return team member credits for frozen users (not owner)
		if (!isUserFrozen(currentUser) || !currentUser) return null;

		const member = currentUserMember();
		if (!member) return null;

		return TeamMethods.getMemberCurrentCreditsBalance(member);
	});

	const teamMemberCreditsTotal = createMemo(() => {
		const currentUser = user();
		// Only return team member credits for frozen users (not owner)
		if (!isUserFrozen(currentUser) || !currentUser) return null;

		const member = currentUserMember();
		if (!member) return null;

		return TeamMethods.getMemberCreditsTotal(member);
	});

	// Determine which credits to use based on subscription_status:
	// - subscription_status === "active" -> use personal credits from API
	// - subscription_status === "frozen" -> use team credits from API
	const remainCredits = createMemo(() => {
		const currentUser = user();
		if (!currentUser) return 0;

		// If user is active, use personal credits from API (or fallback to user store)
		if (currentUser.subscription_status === "active") {
			return personalCredits.data?.credits_balance ?? getUserCreditsBalance(currentUser);
		}

		// If user is frozen and in team with active subscription, show team credits
		if (currentUser.subscription_status === "frozen") {
			const memberCredits = teamMemberCreditsBalance();
			if (memberCredits !== null) {
				return memberCredits;
			}
		}

		// Fallback to personal credits for other statuses
		return getUserCreditsBalance(currentUser);
	});

	// Memoize total calculation from personal credits API
	const personalCreditsTotal = createMemo(() => {
		if (!personalCredits.data) return null;
		return personalCredits.data.credits_balance_breakdowns.reduce(
			(sum, breakdown) => sum + breakdown.credits_received,
			0
		);
	});

	const totalCredits = createMemo(() => {
		const currentUser = user();
		if (!currentUser) return 0;

		// If user is active, use calculated total from API data (or fallback to user store)
		if (currentUser.subscription_status === "active") {
			const apiTotal = personalCreditsTotal();
			if (apiTotal !== null && apiTotal > 0) {
				return apiTotal;
			}
			return getUserCreditsTotal(currentUser);
		}

		// If user is frozen and in team with active subscription, show team credits
		if (currentUser.subscription_status === "frozen") {
			const memberTotal = teamMemberCreditsTotal();
			if (memberTotal !== null) {
				return memberTotal;
			}
		}

		// Fallback to personal credits for other statuses
		return getUserCreditsTotal(currentUser);
	});

	return {
		remainCredits,
		totalCredits
	};
};
